// Common Pagination Component
export const Pagination = ({ currentPage, totalPages, onPageChange, className = "" }) => {
    // Calculate page numbers to display (show at most 7 pages with ellipses)
    const getPageNumbers = () => {
      const pageNumbers = [];
      
      if (totalPages <= 7) {
        // If 7 or fewer pages, show all
        for (let i = 1; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        // Always show first page
        pageNumbers.push(1);
        
        // Show pages around current page
        const startPage = Math.max(2, currentPage - 1);
        const endPage = Math.min(totalPages - 1, currentPage + 1);
        
        // Add ellipsis after first page if needed
        if (startPage > 2) {
          pageNumbers.push('...');
        }
        
        // Add pages around current page
        for (let i = startPage; i <= endPage; i++) {
          pageNumbers.push(i);
        }
        
        // Add ellipsis before last page if needed
        if (endPage < totalPages - 1) {
          pageNumbers.push('...');
        }
        
        // Always show last page
        pageNumbers.push(totalPages);
      }
      
      return pageNumbers;
    };
    
    if (totalPages <= 1) return null;
    
    return (
      <div className={`flex items-center justify-center my-4 ${className}`}>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-3 py-1 mx-1 rounded-md ${
            currentPage === 1
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Previous
        </button>
        
        {getPageNumbers().map((pageNumber, index) => (
          <button
            key={index}
            onClick={() => typeof pageNumber === 'number' && onPageChange(pageNumber)}
            className={`px-3 py-1 mx-1 rounded-md ${
              pageNumber === currentPage
                ? 'bg-blue-500 text-white'
                : pageNumber === '...'
                ? 'bg-transparent text-gray-700 cursor-default'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
            disabled={pageNumber === '...'}
          >
            {pageNumber}
          </button>
        ))}
        
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`px-3 py-1 mx-1 rounded-md ${
            currentPage === totalPages
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Next
        </button>
      </div>
    );
  };
  
  export const TabPagination = ({ 
    pagination, 
    onPageChange, 
    onPageSizeChange,
    className = "",
    showPageSize = true,
    pageSizeOptions = [10, 20, 50, 100]
  }) => {
    if (!pagination) return null;
    
    // Use totalFiltered if available and not undefined/null, otherwise fall back to total
    const itemCount = pagination.totalFiltered != null ? pagination.totalFiltered : pagination.total;
    
    // Don't show pagination if there are fewer items than the page size or no items
    if (itemCount <= 0 || itemCount <= pagination.pageSize) return null;
    
    return (
      <div className={`flex items-center justify-between ${className}`}>
        <div className="flex items-center">
          <span className="text-sm text-gray-700">
            Page {pagination.page} of {pagination.totalPages} 
            {" "}({itemCount} items)
          </span>
        </div>
        <div className="flex items-center">
          {showPageSize && (
            <div className="mr-4">
              <select
                value={pagination.pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className="text-sm border rounded-md py-1 px-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>
                    {size} per page
                  </option>
                ))}
              </select>
            </div>
          )}
          
          <div className="flex">
            <button
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className={`px-2 py-1 text-sm rounded-l-md ${
                pagination.page === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Prev
            </button>
            <button
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className={`px-2 py-1 text-sm rounded-r-md ${
                pagination.page === pagination.totalPages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  };